import React, { useState, useEffect, useCallback, useRef } from 'react';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import styles from './DBStories.module.css';
import { CLIENT_STORIES_API_BASE_URL } from '../../runtimeConfig';
import { acquireApiToken } from '../../auth/acquireApiToken';
import { encryptPayload } from '../../utils/cryptoClient';

const API_BASE = CLIENT_STORIES_API_BASE_URL;
const MAX_TEXT_LENGTH = 255;
const CONTRACT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Sept', 'Oct', 'Nov', 'Dec'];
// Generated by GitHub Copilot - Oct-26-2026
const CONTRACT_DATE_PATTERN = '([Jj][Aa][Nn]|[Ff][Ee][Bb]|[Mm][Aa][Rr]|[Aa][Pp][Rr]|[Mm][Aa][Yy]|[Jj][Uu][Nn]|[Jj][Uu][Ll]|[Aa][Uu][Gg]|[Ss][Ee][Pp][Tt]?|[Oo][Cc][Tt]|[Nn][Oo][Vv]|[Dd][Ee][Cc]) [0-9]{4}';
const ALIAS_USE_ONLY_SUFFIX = ' - Alias Use Only';
const REFERENCED_EARLIER_LABEL = 'Referenced Earlier: Contact POC to Activate.';
const REFERENCED_EARLIER_SUMMARY_PREFIX = '[REFERENCED EARLIER – CONTACT POC TO ACTIVATE]';
const QUARTER_OPTIONS = ['Q1', 'Q2', 'Q3', 'Q4'];
const DEAL_SIGN_MONTH_OPTIONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];

// ── API helpers ───────────────────────────────────────────────────────────────
// Every Client Stories call is authenticated: apiFetch/apiDownload attach a
// Microsoft Entra ID (MSAL) Bearer token acquired via acquireApiToken(). When
// SSO is disabled (local dev) acquireApiToken() returns null and the header is
// skipped, matching the backend which only enforces when Azure AD is configured.
// Pull a human-readable message out of an error response. FastAPI returns
// errors as JSON like {"detail":"..."}; show that message rather than the
// raw JSON body. Falls back to plain text, then to a generic message.
async function errorMessage(res, fallback = 'Request failed') {
  const text = await res.text();
  if (!text) return fallback;
  try {
    const data = JSON.parse(text);
    const detail = data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(d => d?.msg || d).join(', ');
    if (detail) return String(detail);
    if (typeof data?.message === 'string') return data.message;
  } catch { /* not JSON — fall through to raw text */ }
  return text;
}

async function apiFetch(path, opts = {}) {
  const headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
  const token = await acquireApiToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let { body } = opts;
  // Hybrid-encrypt JSON bodies on POST (RSA-wrapped AES key in the
  // x-cryptokey-details header, AES-GCM ciphertext as { value }). Only POST is
  // encrypted; file uploads go through apiUpload (multipart, not JSON).
  if (opts.method === 'POST' && typeof body === 'string') {
    const { value, cryptoKeyDetails } = await encryptPayload(JSON.parse(body));
    headers['x-cryptokey-details'] = cryptoKeyDetails;
    body = JSON.stringify({ value });
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers,
    body,
  });
  if (!res.ok) throw new Error(await errorMessage(res));
  return res.json();
}

async function apiDownload(path, filename) {
  const headers = {};
  const token = await acquireApiToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { headers });
  if (!res.ok) throw new Error('Download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// Multipart upload (file imports). Attaches the Bearer token but does NOT set
// Content-Type — the browser must set it so the multipart boundary is included.
async function apiUpload(path, formData, fallbackMsg = 'Upload failed') {
  const headers = {};
  const token = await acquireApiToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', body: formData, headers });
  if (!res.ok) throw new Error(await errorMessage(res, fallbackMsg));
  return res.json();
}

// ── Add Story Modal ───────────────────────────────────────────────────────────
// Canonical dropdown values mirrored from the Excel upload template's
// data-validation lists ("GCC Client Stories WebApp Upload Template.xlsx"),
// so the Add/Edit form offers exactly the same choices as the spreadsheet.
const EXCEL_DROPDOWNS = {
  industry: [
    'Banking & Cap Mkts', 'Utilities', 'Energy', 'Comms & Media', 'Software & Platforms',
    'CG&S, Retail, Travel', 'Chem & Nat Resources', 'Health', 'Life Sciences', 'Industrials',
    'Insurance', 'High Tech', 'Public Service',
  ],
  market_unit: ['EMEA', 'APAC', 'Americas'],
  referenceability: [
    'Internal', 'External Restricted - Sales Only',
    'Internal | Alias Use Only', 'External Restricted - Sales Only | Alias Use Only',
  ],
  fte_range: ['<100', '100-1000', '1001-5000', '>5000'],
  status: ['Delivered', 'In Progress'],
  gcc_offerings: [
    'GCC Set-up: Assisted Build Out', 'GCC Set-up: Build Operate Transfer', 'GCC Talent Placement',
    'GCC Strategy & Design', 'GCC Reinvention', 'AI for GCC', 'GCC Platform',
  ],
  scope_of_services: [
    'Market Research', 'Vision & Ambition', 'Blueprinting', 'Operating Model Design',
    'Location Strategy', 'Sourcing Strategy', 'High level Org Design', 'Value Case',
    'Implementation Roadmap', 'Legal Entity, Tax & Regulatory Setup', 'Real Estate and Workspace',
    'IT Infrastructure Enablement', 'Program Governance', 'Value Realization', 'Change Management',
    'Capability Design', 'SDLC/PDLC Enhancement', 'Organization Design', 'Enabling Tech Design',
    'Service Management & Governance', 'Transition', 'Employee Value Proposition (EVP)',
    'Talent Acquisition & Onboarding', 'Talent Activation', 'One Organization Culture',
    'Employer Branding', 'Seed Teams', 'Capacity Services', 'GCC Maturity Diagnostics',
    'Operating Model Reinvention', 'Capability Transformation', 'Business Service Optimization',
    'People Development', 'Control Tower & Perf. Reporting', 'Tech Modernization', 'GCC Platform',
    'AI CoE', 'Agent Marketplace', 'AI Academy',
  ],
  gcc_scope: [
    'Banking Products Operations Management', 'Brand Management', 'Commercial Management',
    'Commodity Trading Agri', 'Cross Product Processing and Servicing', 'Customer Service',
    'Data Management', 'Digital & Technology', 'Engineering',
    'Enterprise Project Program & Portfolio Management', 'Enterprise Risk Management',
    'Enterprise Strategy Enablement', 'Finance', 'Human Resources',
    'Infrastructure & Facility Management', 'Innovation', 'Insight and Analytics',
    'Legal & Regulatory Affairs', 'Manufacturing', 'Marketing', 'Medical Affairs',
    'Merchandising', 'Operations', 'Procurement', 'Product Design and Development',
    'Quality Management', 'Research and Development', 'Sales', 'Supply Chain',
    'Sustainability ESG & HSE',
  ],
};

// Keeps a story's existing value selectable when editing, even if it isn't one
// of the canonical Excel options (e.g. legacy or free-text imported records) —
// otherwise opening such a story in the dropdown would silently blank the field.
function withValue(options, value) {
  if (!value || options.includes(value)) return options;
  return [value, ...options];
}
const EMPTY_STORY = {
  client_name: '', industry: '', referenceability: 'Internal', fte_range: '',
  num_ftes: '', quarter: '', deal_sign_month: '', cg: '', gcc_archetype: '', deal_value_mn: '',
  alias_use_only: false,
  referenced_earlier: false,
  partnered_with_ansr: false, new_win: false,
  market_unit: '', gcc_offerings: '', scope_of_services: '', gcc_scope: '',
  gcc_location_scope: '', status: '', contract_start: '', contract_end: '',
  summary: '', context: '', accenture_approach: '', outcomes: '',
  key_point_of_contact: '', ppt_link: '',
};

function buildStoryFormState(story) {
  if (!story) return EMPTY_STORY;
  const initial = { ...EMPTY_STORY };
  Object.keys(EMPTY_STORY).forEach(k => { if (story[k] !== undefined && story[k] !== null) initial[k] = story[k]; });
  const { displayName, displaySummary, isAlias, hasReferencedEarlier } = getStoryDisplayMeta(story);
  initial.client_name = displayName;
  initial.summary = displaySummary;
  initial.alias_use_only = isAlias;
  initial.referenced_earlier = hasReferencedEarlier;
  return initial;
}
function isContractDateInput(value) {
  if (!value) return true;
  const normalizedValue = value.toLowerCase();
  return CONTRACT_MONTHS.some(month => {
    const normalizedMonth = month.toLowerCase();
    if (normalizedMonth.startsWith(normalizedValue)) return true;
    const datePrefix = `${normalizedMonth} `;
    return normalizedValue.startsWith(datePrefix) && /^\d{0,4}$/.test(normalizedValue.slice(datePrefix.length));
  });
}

function validateInput(value) {
  return /^[A-Za-z ]*$/.test(value);
}

// Generated by GitHub Copilot - Oct-24-2026
/** Allows prose characters while excluding markup delimiters and other symbols. */
function isTextareaInput(value) {
  return /^[\p{L}\p{N}\s?,.\-;:'"()!]*$/u.test(value);
}

// Plain form controls, defined at module scope (not inside AddStoryModal) so
// React keeps the same component identity across re-renders — otherwise the
// <input>/<select> DOM nodes get recreated on every keystroke and the input
// loses focus after each character typed.
function FormInput({ label, value, onChange, type = 'text', required = false, placeholder = '', step, min, max, maxLength = MAX_TEXT_LENGTH, pattern, acceptValue }) {
  return (
    <div className={styles.formField}>
      <label className={styles.formLabel}>{label}{required && <span className={styles.req}> *</span>}</label>
      <input className={styles.formInput} type={type} value={value} placeholder={placeholder}
        onChange={e => {
          if (!acceptValue || acceptValue(e.target.value)) onChange(e.target.value);
        }} required={required} step={step} min={min} max={max}
        maxLength={maxLength} pattern={pattern} />
    </div>
  );
}

function FormSelect({ label, value, onChange, options, required = false }) {
  return (
    <div className={styles.formField}>
      <label className={styles.formLabel}>{label}{required && <span className={styles.req}> *</span>}</label>
      <select className={styles.formSelect} value={value} onChange={e => onChange(e.target.value)} required={required}>
        <option value="">Select…</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function FormCheckToggle({ label, checked, onChange, text, icon }) {
  return (
    <div className={styles.formField}>
      <label className={styles.formLabel}>{label}{icon && <span aria-hidden="true"> {icon}</span>}</label>
      <label className={styles.checkToggleRow}>
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
        <span>{text}</span>
      </label>
    </div>
  );
}

function FormTextarea({ label, value, onChange, rows = 3, placeholder = '', maxLength = 250, acceptValue }) {
  return (
    <div className={`${styles.formField} ${styles.formFieldFull}`}>
      <label className={styles.formLabel}>{label}</label>
      <textarea className={styles.formTextarea} rows={rows} value={value} placeholder={placeholder}
        onChange={e => {
          if (!acceptValue || acceptValue(e.target.value)) onChange(e.target.value);
        }} maxLength={maxLength} />
    </div>
  );
}

// Reusable checkbox list for multi-value fields (stored as a "A; B; C" string
// on the form, matching the semicolon-separated convention used across the API).
function MultiCheckList({ label, options, value, onChange, maxLength = MAX_TEXT_LENGTH }) {
  const selected = value ? value.split(/[;,]/).map(s => s.trim()).filter(Boolean) : [];
  // Show any already-selected value that isn't in the canonical option list, so
  // editing a legacy record never hides (and silently drops) its stored values.
  const allOptions = Array.from(new Set([...(options || []), ...selected]));
  const toggle = (opt) => {
    const next = selected.includes(opt) ? selected.filter(o => o !== opt) : [...selected, opt];
    const nextValue = next.join('; ');
    if (nextValue.length > maxLength) return;
    onChange(nextValue);
  };
  return (
    <div className={styles.formField}>
      <label className={styles.formLabel}>{label}</label>
      <div className={styles.checkListBox}>
        {allOptions.map(o => (
          <label key={o} className={styles.checkListItem}>
            <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} />
            <span>{o}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// Same as MultiCheckList, plus a free-text "Add location..." input so users
// can add a GCC location that isn't in the predefined options list.
function LocationMultiCheckList({ label, options, value, onChange }) {
  const [draft, setDraft] = useState('');
  const selected = value ? value.split(/[;,]/).map(s => s.trim()).filter(Boolean) : [];
  const allOptions = Array.from(new Set([...(options || []), ...selected]));
  const toggle = (opt) => {
    const next = selected.includes(opt) ? selected.filter(o => o !== opt) : [...selected, opt];
    const nextValue = next.join('; ');
    if (nextValue.length > 100) return;
    onChange(nextValue);
  };
  const addCustom = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const val = draft.trim();
    if (val && !selected.includes(val)) {
      const nextValue = [...selected, val].join('; ');
      if (nextValue.length > 100) return;
      onChange(nextValue);
    }
    setDraft('');
  };
  return (
    <div className={styles.formField}>
      <label className={styles.formLabel}>{label}</label>
      <div className={styles.checkListBox}>
        {allOptions.map(o => (
          <label key={o} className={styles.checkListItem}>
            <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} />
            <span>{o}</span>
          </label>
        ))}
        <input className={styles.checkListAddInput} type="text" placeholder="Add location…"
          value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={addCustom}
          maxLength={100} pattern="[A-Za-z ]*" />
      </div>
    </div>
  );
}

// story: pass an existing story object to edit it in-place (PUT); omit/null to create a new one (POST).
function AddStoryModal({ filterOptions, story, onClose, onSaved }) {
  const isEdit = Boolean(story);
  const [form, setForm] = useState(() => buildStoryFormState(story));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handler); document.body.style.overflow = ''; };
  }, [onClose]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const normalizedClientName = form.client_name.replace(REFERENCED_EARLIER_TITLE_RE, '').trim();
    const normalizedSummary = form.summary.replace(REFERENCED_EARLIER_SUMMARY_RE, '').trim();
    if (!normalizedClientName) { setError('Client name is required'); return; }
    if (!validateInput(normalizedClientName)) {
      setError('Client Name can contain letters and spaces only');
      return;
    }
    if (!form.industry.trim()) { setError('Industry is required'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        client_name: `${normalizedClientName}${form.alias_use_only ? ALIAS_USE_ONLY_SUFFIX : ''}`,
        summary: form.referenced_earlier
          ? `${REFERENCED_EARLIER_SUMMARY_PREFIX}${normalizedSummary ? `\n\n${normalizedSummary}` : ''}`
          : normalizedSummary,
      };
      delete payload.alias_use_only;
      delete payload.referenced_earlier;
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      if (isEdit) {
        if (story.version !== undefined) payload.expected_version = story.version;
        await apiFetch(`/stories/${story.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        await apiFetch('/stories/', { method: 'POST', body: JSON.stringify(payload) });
      }
      onSaved(); onClose();
    } catch (err) { setError(err.message || 'Failed to save story'); }
    finally { setSaving(false); }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose} role="dialog" aria-modal="true" aria-label="Add story">
      <div className={`${styles.modalBox} ${styles.formModalBox}`} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{isEdit ? 'Edit Story' : 'Add New Story'}</h2>
          <button className={styles.modalCloseX} onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form className={styles.modalBody} onSubmit={handleSubmit}>
          <div className={styles.formGrid}>
            <FormInput label="Client Name" value={form.client_name} onChange={v => set('client_name', v)} required maxLength={100}
              pattern="[A-Za-z ]+" acceptValue={validateInput} />
            <FormSelect label="Industry" value={form.industry} onChange={v => set('industry', v)} options={withValue(EXCEL_DROPDOWNS.industry, form.industry)} required />
            <FormSelect label="Referenceability" value={form.referenceability} onChange={v => set('referenceability', v)} options={withValue(EXCEL_DROPDOWNS.referenceability, form.referenceability)} required />
            <FormSelect label="FTE Range" value={form.fte_range} onChange={v => set('fte_range', v)} options={withValue(EXCEL_DROPDOWNS.fte_range, form.fte_range)} />
            <FormCheckToggle label="Partnered with ANSR?" checked={!!form.partnered_with_ansr} onChange={v => set('partnered_with_ansr', v)} text="Yes, partnered with ANSR" />
            <FormCheckToggle label="New Win?" checked={!!form.new_win} onChange={v => set('new_win', v)} text="Yes, this is a new win" icon="🏅" />
          {/* <FormCheckToggle label="Alias Use Only?" checked={!!form.alias_use_only} onChange={v => set('alias_use_only', v)} text="Yes, Alias Use Only" /> */}
            <FormCheckToggle label="Referenced earlier - contact POC to activate" checked={!!form.referenced_earlier} onChange={v => set('referenced_earlier', v)} text="Yes, Referenced earlier - contact POC to activate" />
            <FormSelect label="Market Unit" value={form.market_unit} onChange={v => set('market_unit', v)} options={withValue(EXCEL_DROPDOWNS.market_unit, form.market_unit)} />
            <MultiCheckList label="GCC Offerings" options={EXCEL_DROPDOWNS.gcc_offerings}
              value={form.gcc_offerings} onChange={v => set('gcc_offerings', v)} />
            <MultiCheckList label="Scope of Services" options={EXCEL_DROPDOWNS.scope_of_services}
              value={form.scope_of_services} onChange={v => set('scope_of_services', v)} />
            <MultiCheckList label="GCC Functions in Scope" options={EXCEL_DROPDOWNS.gcc_scope}
              value={form.gcc_scope} onChange={v => set('gcc_scope', v)} />
            <LocationMultiCheckList label="GCC Location" options={filterOptions.gcc_location_scopes || []}
              value={form.gcc_location_scope} onChange={v => set('gcc_location_scope', v)} />
            <FormSelect label="Status" value={form.status} onChange={v => set('status', v)} options={withValue(EXCEL_DROPDOWNS.status, form.status)} />
            <FormInput label="Contract Start" value={form.contract_start} onChange={v => set('contract_start', v)} placeholder="e.g. Jan 2022" maxLength={30}
              pattern={CONTRACT_DATE_PATTERN} acceptValue={isContractDateInput} />
            <FormInput label="Contract End" value={form.contract_end} onChange={v => set('contract_end', v)} placeholder="e.g. Dec 2025" maxLength={30}
              pattern={CONTRACT_DATE_PATTERN} acceptValue={isContractDateInput} />
          </div>
          <FormTextarea label="Summary" value={form.summary} onChange={v => set('summary', v)} rows={2} placeholder="Short summary (2-3 sentences)" maxLength={250} acceptValue={isTextareaInput} />
          <FormTextarea label="Context" value={form.context} onChange={v => set('context', v)} rows={3} placeholder="Client background, situation, and objectives…" maxLength={250} acceptValue={isTextareaInput} />
          <FormTextarea label="Accenture's Approach" value={form.accenture_approach} onChange={v => set('accenture_approach', v)} rows={3} placeholder="How Accenture helped, solution delivered…" maxLength={250} acceptValue={isTextareaInput} />
          <FormTextarea label="Outcomes" value={form.outcomes} onChange={v => set('outcomes', v)} rows={3} placeholder="Results delivered, business impact, value created…" maxLength={250} acceptValue={isTextareaInput} />
          <FormInput label="Key Point of Contact" value={form.key_point_of_contact} onChange={v => set('key_point_of_contact', v)} placeholder="e.g. John Smith" maxLength={100}
            pattern="[A-Za-z ]+" acceptValue={validateInput} />
          <div className={styles.formFieldFull}>
            <FormInput label="Proposal Deck Link (URL)" value={form.ppt_link} onChange={v => set('ppt_link', v)} type="url" placeholder="e.g. https://sharepoint.com/…" maxLength={250} />
          </div>
          {error && <p className={styles.formError}>{error}</p>}
          <div className={styles.formActions}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>Cancel</button>
            <button type="submit" className={styles.loginBtn} disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Story'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Import Modal ──────────────────────────────────────────────────────────────
function ImportModal({ onClose, onImported }) {
  const [importType, setImportType] = useState('excel'); // 'excel' | 'ppt'
  const [step, setStep] = useState('pick');
  const [pendingFile, setPendingFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mappings, setMappings] = useState({});
  const [uploading, setUploading] = useState(false);
  const [templateDownloading, setTemplateDownloading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef();

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handler); document.body.style.overflow = ''; };
  }, [onClose]);

  // Stages the chosen file locally; the actual upload/parse happens when the
  // user continues, after selection-time size and filename checks pass.
  const stageFile = (file) => {
    if (!file) return;
    setError('');
    setPendingFile(null);
    if (file.size > 3 * 1024 * 1024) {
      setError('File must be no larger than 3 MB.');
      return;
    }
    const validFilename = importType === 'ppt'
      ? /^[A-Za-z0-9 _()-]+\.pptx$/i.test(file.name)
      : /^[A-Za-z0-9 _()-]+\.(xlsx|xls)$/i.test(file.name);
    if (!validFilename) {
      setError(importType === 'ppt'
        ? 'Rename the file using only letters, numbers, spaces, hyphens, underscores, or parentheses, and keep the .pptx extension.'
        : 'Rename the file using only letters, numbers, spaces, hyphens, underscores, or parentheses, and keep the .xlsx or .xls extension.');
      return;
    }
    setPendingFile(file);
  };

  const handleFile = (e) => stageFile(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    stageFile(e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  // Switches between the Excel and PowerPoint import flows; clears any
  // in-progress selection so a stale file isn't submitted under the wrong type.
  const handleTypeChange = (type) => {
    if (type === importType) return;
    setImportType(type);
    setPendingFile(null);
    setError('');
  };

  const handleTemplateDownload = async () => {
    setTemplateDownloading(true); setError('');
    try {
      if (importType === 'ppt') {
        await apiDownload('/import/template/ppt', 'Client Story Template.pptx');
      } else {
        // Must hit the dedicated single-row template endpoint, not /export/excel
        // (which dumps every client story currently in the database).
        await apiDownload('/import/template/excel', 'GCC Client Stories WebApp Upload Template.xlsx');
      }
    }
    catch (err) { setError(err.message || 'Failed to download template'); }
    finally { setTemplateDownloading(false); }
  };

  const handleNext = async () => {
    if (!pendingFile) return;
    setUploading(true); setError('');
    try {
      if (importType === 'ppt') {
        // PPT files are parsed and imported in a single step server-side —
        // there's no column-mapping stage like the Excel flow.
        const form = new FormData();
        form.append('file', pendingFile);
        const data = await apiUpload('/import/ppt', form, 'Failed to import PPT');
        setResult(data); setStep('done'); onImported();
        return;
      }
      const form = new FormData();
      form.append('file', pendingFile);
      const data = await apiUpload('/import/excel/preview', form, 'Failed to parse file');
      setPreview(data);
      const initial = {};
      data.columns.forEach(col => { initial[col] = data.suggestions[col] || ''; });
      setMappings(initial);
      setStep('preview');
    } catch (err) { setError(err.message || (importType === 'ppt' ? 'Failed to import PPT' : 'Failed to parse file')); }
    finally { setUploading(false); }
  };

  const handleConfirm = async () => {
    setConfirming(true); setError('');
    try {
      const mappingList = Object.entries(mappings)
        .filter(([, v]) => v && v !== '__skip__')
        .map(([source_col, target_field]) => ({ source_col, target_field }));
      const data = await apiFetch('/import/excel/confirm', {
        method: 'POST',
        body: JSON.stringify({ file_b64: preview.file_b64, mappings: mappingList, transposed: preview.transposed }),
      });
      setResult(data); setStep('done'); onImported();
    } catch (err) { setError(err.message || 'Import failed'); }
    finally { setConfirming(false); }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose} role="dialog" aria-modal="true" aria-label="Import stories">
      <div className={`${styles.modalBox} ${styles.formModalBox}`} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Import Stories</h2>
          <button className={styles.modalCloseX} onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={styles.modalBody}>
          {step === 'pick' && (
            <div className={styles.importPickZone}>
              <div className={styles.importTypeTabs} role="tablist" aria-label="Import file type">
                <button type="button" role="tab" aria-selected={importType === 'excel'}
                  className={`${styles.importTypeTab} ${importType === 'excel' ? styles.importTypeTabActive : ''}`}
                  onClick={() => handleTypeChange('excel')}>
                  <span aria-hidden="true">📊</span> Excel (.xlsx)
                </button>
                <button type="button" role="tab" aria-selected={importType === 'ppt'}
                  className={`${styles.importTypeTab} ${importType === 'ppt' ? styles.importTypeTabActive : ''}`}
                  onClick={() => handleTypeChange('ppt')}>
                  <span aria-hidden="true">📄</span> PowerPoint (.pptx)
                </button>
              </div>

              {importType === 'excel' ? (
                <div className={styles.importInfoBox}>
                  <p className={styles.importInfoTitle}>Any Excel format accepted</p>
                  <p className={styles.importInfoText}>
                    Upload your file and we'll detect the columns automatically. You'll confirm the mapping
                    before anything is imported.
                  </p>
                  <button type="button" className={styles.importTemplateLink}
                    onClick={handleTemplateDownload} disabled={templateDownloading}>
                    <span aria-hidden="true">⬇</span> {templateDownloading ? 'Preparing template…' : 'Download Excel template'}
                  </button>
                </div>
              ) : (
                <div className={styles.importInfoBox}>
                  <p className={styles.importInfoTitle}>PowerPoint format</p>
                  <p className={styles.importInfoText}>
                    One slide per story. Slide title → Client Name. Use{' '}
                    <code className={styles.importInfoCode}>Label: Value</code> lines for each field.
                  </p>
                  <button type="button" className={styles.importTemplateLink}
                    onClick={handleTemplateDownload} disabled={templateDownloading}>
                    <span aria-hidden="true">⬇</span> {templateDownloading ? 'Preparing template…' : 'Download PPT template'}
                  </button>
                </div>
              )}

              {error && <p className={styles.formError} role="alert">{error}</p>}

              <label htmlFor="import-file"
                className={`${styles.importDropZone} ${dragOver ? styles.importDropZoneActive : ''}`}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}>
                <input ref={fileRef} type="file" accept={importType === 'ppt' ? '.pptx' : '.xlsx,.xls'}
                  onChange={handleFile} className={styles.fileInput} id="import-file" />
                <svg className={styles.importDropIcon} width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 16V4m0 0l-4 4m4-4l4 4M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
                </svg>
                <p className={styles.importDropText}>
                  {pendingFile ? pendingFile.name : <>Drop file here or <span className={styles.importBrowseText}>browse</span></>}
                </p>
                <p className={styles.importDropSubtext}>{importType === 'ppt' ? '.pptx' : '.xlsx, .xls'}</p>
              </label>

              <div className={styles.formActions}>
                <button type="button" className={styles.cancelBtn} onClick={onClose}>Cancel</button>
                <button type="button" className={styles.loginBtn} onClick={handleNext} disabled={!pendingFile || uploading}>
                  {uploading
                    ? (importType === 'ppt' ? 'Importing…' : 'Uploading…')
                    : (importType === 'ppt' ? 'Import' : 'Next: Map Columns →')}
                </button>
              </div>
            </div>
          )}
          {step === 'preview' && preview && (
            <>
              <p className={styles.importHint}>Map Excel columns to story fields. Unneeded columns can be skipped.</p>
              <div className={styles.mappingGrid}>
                {preview.columns.map(col => (
                  <div key={col} className={styles.mappingRow}>
                    <span className={styles.mappingSource}>{col}</span>
                    <span className={styles.mappingArrow}>→</span>
                    <select className={styles.formSelect} value={mappings[col] || ''}
                      onChange={e => setMappings(prev => ({ ...prev, [col]: e.target.value }))}>
                      <option value="__skip__">— Skip —</option>
                      {(preview.known_fields || []).map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              {error && <p className={styles.formError}>{error}</p>}
              <div className={styles.formActions}>
                <button className={styles.cancelBtn} onClick={() => setStep('pick')}>Back</button>
                <button className={styles.loginBtn} onClick={handleConfirm} disabled={confirming}>
                  {confirming ? 'Importing…' : 'Confirm Import'}
                </button>
              </div>
            </>
          )}
          {step === 'done' && result && (
            <div className={styles.importDone}>
              <div className={styles.importSuccessIcon}>✓</div>
              <p className={styles.importSuccessMsg}>Import complete — <strong>{result.imported ?? result.added ?? '?'}</strong> stories added.</p>
              <button className={styles.loginBtn} onClick={onClose}>Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Story Detail Modal ────────────────────────────────────────────────────────
// "Sept" (not the locale-default "Sep") matches the reference design's date format exactly.
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];

/**
 * Formats an ISO timestamp as "9 Sept 2026 at 11:14:12 GMT" (UTC).
 * Returns null for missing/invalid input so callers can hide the row cleanly.
 */
function formatLastModified(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const day = d.getUTCDate();
  const month = MONTH_ABBR[d.getUTCMonth()];
  const year = d.getUTCFullYear();
  const pad = (n) => String(n).padStart(2, '0');
  const time = `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
  return `${day} ${month} ${year} at ${time} GMT`;
}

function StoryModal({ story, onClose, onDelete, onEdit, onDownload, exporting = '' }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handler); document.body.style.overflow = ''; };
  }, [onClose]);

  const { displayName, displaySummary, isAlias, hasReferencedEarlier } = getStoryDisplayMeta(story);

  const Field = ({ label, value, accent = false }) => {
    if (value === null || value === undefined || value === '') return null;
    return (
      <div className={styles.detailBox}>
        <span className={styles.detailBoxLabel}>{label}</span>
        <span className={`${styles.detailBoxValue} ${accent ? styles.detailBoxValueAccent : ''}`}>
          {String(value)}{accent && <span aria-hidden="true"> 🏅</span>}
        </span>
      </div>
    );
  };

  const LongField = ({ label, value, tone = 'indigo' }) => {
    if (!value) return null;
    const toneClass = tone === 'purple' ? styles.longBoxPurple : tone === 'green' ? styles.longBoxGreen : styles.longBoxIndigo;
    const labelClass = tone === 'purple' ? styles.longLabelPurple : tone === 'green' ? styles.longLabelGreen : styles.longLabelIndigo;
    return (
      <div className={styles.detailLongWrap}>
        <p className={`${styles.detailSectionLabel} ${labelClass}`}>{label}</p>
        <div className={`${styles.detailLongField} ${toneClass}`}>
          <p className={styles.detailLongValue}>{value}</p>
        </div>
      </div>
    );
  };

  const TagRow = ({ label, value, variant = 'indigo' }) => {
    if (!value) return null;
    const tags = value.split(/[;,]/).map(t => t.trim()).filter(Boolean);
    if (!tags.length) return null;
    const chipClass = variant === 'teal' ? styles.chipTeal : styles.chipIndigo;
    return (
      <div className={styles.detailTagRow}>
        <p className={styles.detailSectionLabel}>{label}</p>
        <div className={styles.tagList}>{tags.map(t => <span key={t} className={`${styles.chip} ${chipClass}`}>{t}</span>)}</div>
      </div>
    );
  };

  const showFullStory = !story.context && !story.accenture_approach && !story.outcomes;
  const lastModified = formatLastModified(story.last_modified);

  const handleDeleteClick = () => onDelete && onDelete(story);
  const handleEditClick = () => onEdit && onEdit(story);
  const downloadBusy = exporting === 'story-excel' || exporting === 'story-ppt';

  return (
    <div className={styles.modalOverlay} onClick={onClose} role="dialog" aria-modal="true" aria-label="Story detail">
      <div className={`${styles.modalBox} ${styles.detailModalBox}`} onClick={e => e.stopPropagation()}>
        <div className={styles.modalScrollArea}>
          <div className={`${styles.cardClassBar} ${referenceabilityBarClass(story.referenceability)}`}>
            {story.referenceability || 'Internal'}
          </div>
          <div className={styles.modalHeader}>
            <div>
              {(isAlias || hasReferencedEarlier) && (
                <div className={styles.modalHeaderBadges}>
                  {isAlias && <span className={styles.badgeStatus}>Alias Use Only</span>}
                  {hasReferencedEarlier && <span className={styles.badgeStatus}>{REFERENCED_EARLIER_LABEL}</span>}
                </div>
              )}
              <h2 className={styles.modalTitle}>{displayName}</h2>
              {story.industry && <p className={styles.modalIndustryText}>{story.industry}</p>}
            </div>
            <button className={styles.modalCloseX} onClick={onClose} aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.detailGrid}>
              <Field label="Market Unit" value={story.market_unit} />
              <Field label="New Win" value={story.new_win === true ? 'Yes' : story.new_win === false ? 'No' : null} accent={story.new_win === true} />
              <Field label="FTE Range" value={story.fte_range || story.num_ftes} />
              <Field label="GCC Location" value={story.gcc_location_scope} />
              <Field label="Partnered with ANSR" value={story.partnered_with_ansr === true ? 'Yes' : story.partnered_with_ansr === false ? 'No' : null} />
              <Field label="Status" value={story.status} />
              <Field label="Contract Start" value={story.contract_start} />
              <Field label="Contract End" value={story.contract_end} />
              <Field label="GCC Archetype" value={story.gcc_archetype} />
              <Field label="Deal Construct" value={story.deal_construct} />
              <Field label="Start Year" value={story.engagement_start_year} />
              <Field label="Go-Live Date" value={story.go_live_date} />
              <Field label="Key Point of Contact" value={story.key_point_of_contact} />
            </div>
            <TagRow label="GCC Offerings" value={story.gcc_offerings} variant="indigo" />
            <TagRow label="Scope of Services" value={story.scope_of_services} variant="teal" />
            <TagRow label="GCC Functions in Scope" value={story.gcc_scope} variant="indigo" />
            <TagRow label="Tags" value={story.tags} variant="indigo" />
            {displaySummary && (
              <div className={styles.detailLongWrap}>
                <p className={styles.detailSectionLabel}>Summary</p>
                <p className={styles.summaryText}>{displaySummary}</p>
              </div>
            )}
            <LongField label="Context" value={story.context} tone="indigo" />
            <LongField label="Accenture's Approach" value={story.accenture_approach} tone="purple" />
            <LongField label="Outcomes" value={story.outcomes} tone="green" />
            {showFullStory && <LongField label="Full Story" value={story.full_story} tone="indigo" />}
            {story.ppt_link && (
              <a href={story.ppt_link} target="_blank" rel="noopener noreferrer" className={styles.pptLink}>View Proposal Deck ↗</a>
            )}
          </div>
        </div>
        <div className={styles.modalFooterBar}>
          <span className={styles.footerMeta}>{lastModified ? `Last modified: ${lastModified}` : ''}</span>
          <div className={styles.footerActions}>
            {onDownload && (
              <>
                <button type="button" className={styles.toolbarBtn} onClick={() => onDownload(story, 'excel')} disabled={downloadBusy}>
                  {exporting === 'story-excel' ? 'Downloading…' : 'Download Excel'}
                </button>
                <button type="button" className={`${styles.toolbarBtn} ${styles.toolbarBtnPpt}`} onClick={() => onDownload(story, 'ppt')} disabled={downloadBusy}>
                  {exporting === 'story-ppt' ? 'Downloading…' : 'Download PPT'}
                </button>
              </>
            )}
            <button type="button" className={styles.deleteBtn} onClick={handleDeleteClick}>Delete</button>
            <button type="button" className={styles.toolbarBtnPrimary} onClick={handleEditClick}>Edit</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Some imported client names carry a trailing " - Alias Use Only" marker
// (used when a client must be referenced only by an alias/category name).
// Strip it from the displayed title and surface it as its own badge instead.
const ALIAS_SUFFIX_RE = /\s*-\s*Alias Use Only\s*$/i;
const REFERENCED_EARLIER_TITLE_RE = /\s*\[\s*REFERENCED EARLIER(?:,\s*(?:PLS|PLEASE)\s+REACH OUT TO POC|\s*[-–]\s*CONTACT POC TO ACTIVATE)(?:\s+TO ACTIVATE WITH CLIENT)?\s*\]\s*$/i;
const REFERENCED_EARLIER_SUMMARY_RE = /^\s*\[\s*REFERENCED EARLIER(?:,\s*(?:PLS|PLEASE)\s+REACH OUT TO POC|\s*[-–]\s*CONTACT POC TO ACTIVATE)(?:\s+TO ACTIVATE WITH CLIENT)?\s*\]\s*/i;

/**
 * Derives display-safe title/summary values and UI flags from story marker text.
 * This preserves the stored payload while moving editorial markers into badges.
 */
function getStoryDisplayMeta(story) {
  const rawName = story?.client_name || '';
  const rawSummary = story?.summary || '';
  const { displayName: aliasStrippedName, isAlias } = splitAliasLabel(rawName);
  const hasReferencedEarlier = REFERENCED_EARLIER_TITLE_RE.test(aliasStrippedName)
    || REFERENCED_EARLIER_SUMMARY_RE.test(rawSummary);
  const displayName = aliasStrippedName.replace(REFERENCED_EARLIER_TITLE_RE, '').trim();
  const displaySummary = rawSummary.replace(REFERENCED_EARLIER_SUMMARY_RE, '').trim();
  return { displayName, displaySummary, isAlias, hasReferencedEarlier };
}

function splitAliasLabel(clientName) {
  const name = clientName || '';
  const isAlias = ALIAS_SUFFIX_RE.test(name);
  return { displayName: isAlias ? name.replace(ALIAS_SUFFIX_RE, '').trim() : name, isAlias };
}

// ── Story Card ────────────────────────────────────────────────────────────────
// Maps a story's referenceability to the colored header-strip style/label
// shown at the top of each card (matches the reference design's three tones).
function referenceabilityBarClass(referenceability) {
  const r = (referenceability || '').toLowerCase();
  if (r.includes('restricted')) return styles.classBarRestricted;
  if (r.includes('unrestricted')) return styles.classBarUnrestricted;
  return '';
}

function StoryCard({ story, onClick, selectionMode, isSelected, onSelect }) {
  const handleClick = () => selectionMode ? onSelect(story.id) : onClick(story);
  const { displayName, displaySummary, isAlias, hasReferencedEarlier } = getStoryDisplayMeta(story);
  return (
    <article
      className={`${styles.card} ${selectionMode && isSelected ? styles.cardSelected : ''}`}
      onClick={handleClick}
      role={selectionMode ? 'checkbox' : 'button'}
      aria-checked={selectionMode ? isSelected : undefined}
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && handleClick()}
      aria-label={`${selectionMode ? (isSelected ? 'Deselect' : 'Select') : 'View'} story: ${displayName}`}>
      {selectionMode && (
        <div className={styles.cardCheckbox}>
          <input type="checkbox" checked={isSelected} onChange={() => onSelect(story.id)}
            onClick={e => e.stopPropagation()} aria-hidden="true" tabIndex={-1} readOnly />
        </div>
      )}
      <div className={`${styles.cardClassBar} ${referenceabilityBarClass(story.referenceability)}`}>
        <span className={styles.cardClassBarLabel}>{story.referenceability || 'Internal'}</span>
        {isAlias && <span className={styles.cardClassBarAlias}>Alias Use Only</span>}
      </div>
      <div className={styles.cardBody}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitleCol}>
            <h3 className={styles.cardName}>{displayName}</h3>
          </div>
          <div className={styles.cardBadges}>
            {story.new_win && (
              <span className={styles.badgeWin}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2l2.9 6.26L21 9.27l-4.5 4.38L17.8 21 12 17.77 6.2 21l1.3-7.35L3 9.27l6.1-1.01L12 2z" />
                </svg>
                New Win
              </span>
            )}
            {story.status && <span className={styles.badgeStatus}>{story.status}</span>}
          </div>
        </div>
        {story.industry && <p className={styles.cardIndustry}>{story.industry}</p>}
        {displaySummary && <p className={styles.cardSummary}>{displaySummary}</p>}
        {(story.market_unit || story.fte_range || story.num_ftes || hasReferencedEarlier) && (
          <div className={styles.cardInfoRow}>
            {(story.market_unit || story.fte_range || story.num_ftes) && (
              <div className={styles.cardChips}>
                {story.market_unit && <span className={`${styles.chip} ${styles.chipTeal}`}>{story.market_unit}</span>}
                {(story.fte_range || story.num_ftes) && (
                  <span className={`${styles.chip} ${styles.chipIndigo}`}>{story.fte_range || story.num_ftes}</span>
                )}
              </div>
            )}
            {hasReferencedEarlier && <span className={`${styles.chip} ${styles.chipReference} ${styles.cardReferenceNote}`}>{REFERENCED_EARLIER_LABEL}</span>}
          </div>
        )}
        {story.gcc_location_scope && (
          <div className={styles.cardMeta}>
            <svg className={styles.cardMetaIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 21s-7-6.5-7-11a7 7 0 1114 0c0 4.5-7 11-7 11z" />
              <circle cx="12" cy="10" r="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {story.gcc_location_scope}
          </div>
        )}
      </div>
    </article>
  );
}

// ── Filter Sidebar ────────────────────────────────────────────────────────────
function TextFilter({ label, fkey, placeholder = '', filters, onChange, maxLength = MAX_TEXT_LENGTH, pattern }) {
  return (
    <div className={styles.filterGroup}>
      <label className={styles.filterGroupLabel}>{label}</label>
      <input className={styles.filterInput} type="text" placeholder={placeholder}
        value={filters[fkey]} onChange={e => {
          if (!pattern || pattern.test(e.target.value)) onChange(fkey, e.target.value);
        }} maxLength={maxLength} />
    </div>
  );
}

function SelectFilter({ label, fkey, optKey, boolOpts = false, staticOptions, filters, filterOptions, onChange }) {
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  useEffect(() => {
    const closeOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) menuRef.current.open = false;
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape' && menuRef.current?.open) {
        menuRef.current.open = false;
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('focusin', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('focusin', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);
  // staticOptions pins the dropdown to a canonical list (the Add/Edit form's
  // Excel-template values) instead of the backend-derived values in filterOptions.
  const sourceValues = staticOptions || filterOptions[boolOpts ? fkey : optKey] || [];
  const options = sourceValues.map(value => ({
    value: String(value),
    label: boolOpts ? (value ? 'Yes' : 'No') : String(value),
  }));
  if (options.length === 0) return null;
  const selected = Array.isArray(filters[fkey]) ? filters[fkey] : [];
  const selectedLabel = selected.length > 1
    ? `${selected.length} selected`
    : options.find(option => option.value === selected[0])?.label || 'All';
  const selectOption = (value) => {
    onChange(fkey, selected.includes(value)
      ? selected.filter(option => option !== value)
      : [...selected, value]);
  };
  return (
    <div className={styles.filterGroup}>
      <span id={`filter-label-${fkey}`} className={styles.filterGroupLabel}>{label}</span>
      <details ref={menuRef} className={styles.filterSelectWrap}>
        <summary ref={triggerRef} className={styles.filterGroupSelect} aria-label={`${label}: ${selectedLabel}`}>
          <span className={styles.filterSelectedLabel}>{selectedLabel}</span>
          <span className={styles.selectChevron} aria-hidden="true" />
        </summary>
        <div className={styles.filterDropdownMenu} role="group" aria-labelledby={`filter-label-${fkey}`}>
          <button type="button" className={styles.filterClearSelection} disabled={!selected.length}
            onClick={() => onChange(fkey, [])}>Clear selection</button>
          {options.map(option => (
            <label key={option.value} className={styles.filterDropdownOption}>
              <input type="checkbox" checked={selected.includes(option.value)}
                onChange={() => selectOption(option.value)} />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </details>
    </div>
  );
}

// Boolean filter rendered as a pair of Yes/No radio buttons (no dropdown).
// Clicking the already-selected option clears it. Value stays array-shaped
// (['true'] / ['false'] / []) so getFilterQueryParams keeps working unchanged.
function RadioFilter({ label, fkey, filters, onChange }) {
  const selected = Array.isArray(filters[fkey]) ? filters[fkey] : [];
  const current = selected[0] ?? '';
  const pick = (value) => onChange(fkey, current === value ? [] : [value]);
  const OPTIONS = [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }];
  return (
    <div className={styles.filterGroup}>
      <span id={`filter-label-${fkey}`} className={styles.filterGroupLabel}>{label}</span>
      <div className={styles.filterRadioRow} role="radiogroup" aria-labelledby={`filter-label-${fkey}`}>
        {OPTIONS.map(option => (
          <label key={option.value} className={styles.filterRadioOption}>
            <input type="radio" name={`filter-${fkey}`} value={option.value}
              checked={current === option.value}
              onClick={() => pick(option.value)}
              onChange={() => {}} />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function FilterSidebar({ filters, filterOptions, onChange, onReset }) {
  const hasActive = Object.values(filters).some(value => Array.isArray(value) ? value.length > 0 : Boolean(value));

  return (
    <aside className={styles.filterSidebar} aria-label="Filters">
      <div className={styles.filterSidebarHeader}>
        <span className={styles.filterSidebarTitle}>FILTERS</span>
        {hasActive && (
          <button className={styles.resetBtn} onClick={onReset}>Reset all</button>
        )}
      </div>

      <TextFilter label="Keyword Search" fkey="search_text" placeholder="Search stories..." filters={filters} onChange={onChange} maxLength={100} pattern={/^[\p{L} ]*$/u}/>
      <TextFilter label="Client Name" fkey="client_name" placeholder="e.g. AB InBev" filters={filters} onChange={onChange} maxLength={100} pattern={/^[\p{L} ]*$/u}/>
      <SelectFilter label="Industry" fkey="industry" optKey="industries" staticOptions={EXCEL_DROPDOWNS.industry} filters={filters} filterOptions={filterOptions} onChange={onChange} />
      <SelectFilter label="FTE Range" fkey="fte_range" optKey="fte_ranges" staticOptions={EXCEL_DROPDOWNS.fte_range} filters={filters} filterOptions={filterOptions} onChange={onChange} />
       <SelectFilter label="Market Unit" fkey="market_unit" optKey="market_units" filters={filters} filterOptions={filterOptions} onChange={onChange} />
      <SelectFilter label="GCC Offerings" fkey="gcc_offerings" optKey="gcc_offerings_list" staticOptions={EXCEL_DROPDOWNS.gcc_offerings} filters={filters} filterOptions={filterOptions} onChange={onChange} />
      <SelectFilter label="GCC Functions in Scope" fkey="gcc_scope" optKey="gcc_scopes" staticOptions={EXCEL_DROPDOWNS.gcc_scope} filters={filters} filterOptions={filterOptions} onChange={onChange} />
      <SelectFilter label="Scope of Services" fkey="scope_of_services" optKey="scope_of_services_list" staticOptions={EXCEL_DROPDOWNS.scope_of_services} filters={filters} filterOptions={filterOptions} onChange={onChange} />
      <SelectFilter label="GCC Location" fkey="gcc_location_scope" optKey="gcc_location_scopes" filters={filters} filterOptions={filterOptions} onChange={onChange} />
      <SelectFilter label="Status" fkey="status" optKey="statuses" filters={filters} filterOptions={filterOptions} onChange={onChange} />
      <SelectFilter label="Referenceability" fkey="referenceability" optKey="referenceabilities" staticOptions={EXCEL_DROPDOWNS.referenceability} filters={filters} filterOptions={filterOptions} onChange={onChange} />
      <SelectFilter label="Partnered with ANSR" fkey="partnered_with_ansr" boolOpts filters={filters} filterOptions={filterOptions} onChange={onChange} />
      <RadioFilter label="New Win" fkey="new_win" filters={filters} onChange={onChange} />
      <RadioFilter label="Referenced Earlier" fkey="referenced_earlier" filters={filters} onChange={onChange} />
    </aside>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
function getFilterQueryParams(filters) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      if (!value.length) return;
      if ((key === 'new_win' || key === 'partnered_with_ansr' || key === 'referenced_earlier') && value.length > 1) return;
      // Join multi-select values with the ASCII unit separator (\x1f) so option
      // values that themselves contain commas ("CG&S, Retail, Travel") or pipes
      // ("Internal | Alias Use Only") stay intact as a single token. The
      // backend splits incoming filter values on the same character.
      params.append(key, value.join('\x1f'));
    } else if (value !== '' && value !== null) {
      params.append(key, value);
    }
  });
  return params;
}

const EMPTY_FILTERS = {
  search_text: '',
  client_name: '',
  industry: '',
  fte_range: '',
  market_unit: '',
  gcc_offerings: '',
  gcc_scope: '',
  scope_of_services: '',
  gcc_location_scope: '',
  new_win: '',
  status: '',
  referenceability: '',
  partnered_with_ansr: '',
  referenced_earlier: '',
};

const DBStories = () => {
  const pageRef = useRef(null);
  useEffect(() => {
    const page = pageRef.current;
    const navbar = page?.querySelector('nav[aria-label="Main navigation"]');
    if (!navbar) return undefined;

    const updateNavbarHeight = () => {
      const next = `${navbar.getBoundingClientRect().height}px`;
      const current = page.style.getPropertyValue('--navbar-height');
      if (current !== next) {
        page.style.setProperty('--navbar-height', next);
      }
    };

    updateNavbarHeight();
    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      window.requestAnimationFrame(updateNavbarHeight);
    });
    observer.observe(navbar);
    return () => observer.disconnect();
  }, []);
  // /clientstories is SSO-protected (wrapped in <ProtectedRoute>); every CRUD
  // action (add/edit/delete/import/export) carries an Azure AD Bearer token.
  const [stories, setStories] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedStory, setSelectedStory] = useState(null);
  // null = closed, 'new' = add mode, <story object> = edit mode
  const [storyForm, setStoryForm] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [exporting, setExporting] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const fetchFilterOptions = useCallback(async () => {
    let options = {};
    try {
      options = await apiFetch('/filters/options');
    } catch {
      options = {};
    }
    // Yes/No filters always offer both choices (No, Yes) regardless of what
    // the current records contain — otherwise an option disappears whenever no
    // story has that value yet (e.g. referenced_earlier with no "true" rows).
    const booleanOptions = {};
    ['new_win', 'partnered_with_ansr', 'referenced_earlier'].forEach(field => {
      booleanOptions[field] = [false, true];
    });
    setFilterOptions({ ...options, ...booleanOptions });
  }, []);

  const fetchStories = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = getFilterQueryParams(filters);
      const data = await apiFetch(`/stories/?${params}`);
      setStories(data);
    } catch {
      setError('Failed to load stories. Please try again.');
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchFilterOptions(); }, [fetchFilterOptions]);
  useEffect(() => {
    const timer = setTimeout(fetchStories, 300);
    return () => clearTimeout(timer);
  }, [filters, fetchStories]);

  const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  const enterSelectionMode = () => setSelectionMode(true);
  const exitSelectionMode = () => { setSelectionMode(false); setSelectedIds(new Set()); };
  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => setSelectedIds(selectedIds.size === stories.length ? new Set() : new Set(stories.map(s => s.id)));

  const handleBulkDelete = async () => {
    if (!selectedIds.size) return;
    if (!window.confirm(`Permanently delete ${selectedIds.size} ${selectedIds.size === 1 ? 'story' : 'stories'}? This cannot be undone.`)) return;
    setIsBulkDeleting(true);
    try {
      // Single atomic call instead of N parallel DELETE requests — the
      // backend locks once, removes every id, and never throws on partial
      // results, so a large "select all" delete can't be reported as
      // failed just because one of many concurrent requests was slow.
      const result = await apiFetch('/stories/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ ids: [...selectedIds] }),
      });
      setSelectedIds(new Set());
      setSelectionMode(false);
      await fetchStories();
      if (result.not_found && result.not_found.length) {
        setError(`${result.not_found.length} ${result.not_found.length === 1 ? 'story was' : 'stories were'} already removed.`);
      }
    } catch { setError('Failed to delete stories. Please try again.'); }
    finally { setIsBulkDeleting(false); }
  };

  const handleDeleteStory = async (story) => {
    if (!window.confirm(`Permanently delete "${story.client_name}"? This cannot be undone.`)) return;
    try {
      await apiFetch(`/stories/${story.id}`, { method: 'DELETE' });
      setSelectedStory(null);
      await fetchStories();
    } catch { setError('Failed to delete story. Please try again.'); }
  };

  const handleEditStory = (story) => {
    setSelectedStory(null);
    setStoryForm(story);
  };

  const buildFilterParams = (selectedOnly = false) => {
    const params = new URLSearchParams();
    if (selectedOnly && selectionMode && selectedIds.size) {
      [...selectedIds].forEach(id => params.append('ids', String(id)));
      return params.toString();
    }
    return getFilterQueryParams(filters).toString();
  };

  const handleExcelDownload = async (selectedOnly = false) => {
    setExporting('excel');
    try { await apiDownload(`/export/excel?${buildFilterParams(selectedOnly)}`, 'client_stories.xlsx'); }
    catch { setError('Excel export failed.'); }
    finally { setExporting(''); }
  };

  const handlePptDownload = async (selectedOnly = false) => {
    setExporting('ppt');
    try { await apiDownload(`/export/ppt?${buildFilterParams(selectedOnly)}`, 'client_stories.pptx'); }
    catch { setError('PPT export failed.'); }
    finally { setExporting(''); }
  };

  // Direct download of a single story from the detail pop-up — reuses the
  // existing export endpoints, scoped to one record via the `ids` query param.
  const handleStoryDownload = async (story, format) => {
    if (!story?.id) return;
    const ext = format === 'ppt' ? 'pptx' : 'xlsx';
    const safeName = (getStoryDisplayMeta(story).displayName || 'story')
      .replace(/[^\w.-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60) || 'story';
    setExporting(`story-${format}`);
    try { await apiDownload(`/export/${format}?ids=${story.id}`, `${safeName}.${ext}`); }
    catch { setError(`${format === 'ppt' ? 'PPT' : 'Excel'} download failed.`); }
    finally { setExporting(''); }
  };

  return (
    <div ref={pageRef} className={styles.page}>
      <Navbar />
      <div className={styles.heroPadding} />

      {/* ── Hero ── */}
      {/* <section className={styles.heroSection} aria-labelledby="dbstories-heading">
        <div aria-hidden="true" className={styles.bokehContainer}>
          <div className={styles.blobLeft} /><div className={styles.blobCenter} /><div className={styles.blobRight} />
        </div>
        <div className={styles.heroContent}>
          <p className={styles.eyebrow}>GCC CLIENT STORIES DATABASE</p>
          <h1 id="dbstories-heading" className={styles.heroHeading}>Client Stories</h1>
          <p className={styles.heroSubtext}>Search and explore GCC engagement stories from across our portfolio.</p>
        </div>
      </section> */}

      {/* ── Body: sidebar + main ── */}
      <div className={styles.pageBody}>

        {/* Filter Sidebar */}
        <FilterSidebar
          filters={filters}
          filterOptions={filterOptions}
          onChange={handleFilterChange}
          onReset={() => setFilters(EMPTY_FILTERS)}
        />

        {/* Main content */}
        <div className={styles.mainArea}>

          {/* Toolbar */}
          <div className={styles.toolbar}>
            <div>
              <h2 className={styles.toolbarTitle}>GCC Client Stories Repository</h2>
              <p className={styles.toolbarSubtitle}>
                {selectionMode
                  ? `${selectedIds.size} of ${stories.length} selected`
                  : loading ? 'Loading…' : `${stories.length} ${stories.length === 1 ? 'story' : 'stories'} found`}
              </p>
            </div>
            <div className={styles.toolbarActions}>
              {selectionMode ? (
                <>
                  <button className={styles.toolbarBtn} onClick={toggleSelectAll}>
                    {selectedIds.size === stories.length ? 'Deselect All' : 'Select All'}
                  </button>
                  <button className={styles.toolbarBtn} onClick={() => handleExcelDownload(true)} disabled={!selectedIds.size || exporting === 'excel'}>
                    {exporting === 'excel' ? 'Exporting…' : `Excel (${selectedIds.size})`}
                  </button>
                  <button className={`${styles.toolbarBtn} ${styles.toolbarBtnPpt}`} onClick={() => handlePptDownload(true)} disabled={!selectedIds.size || exporting === 'ppt'}>
                    {exporting === 'ppt' ? 'Exporting…' : `PPT (${selectedIds.size})`}
                  </button>
                  <button className={styles.toolbarBtn} onClick={exitSelectionMode}>Cancel</button>
                  <button
                    className={`${styles.toolbarBtn} ${styles.toolbarBtnDelete}`}
                    onClick={handleBulkDelete}
                    disabled={!selectedIds.size || isBulkDeleting}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <polyline points="3 6 5 6 21 6" /><path strokeLinecap="round" strokeLinejoin="round" d="M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" />
                    </svg>
                    {isBulkDeleting ? 'Deleting…' : `Delete ${selectedIds.size} Selected`}
                  </button>
                </>
              ) : (
                <>
                  <button className={styles.toolbarBtn} onClick={handleExcelDownload} disabled={exporting === 'excel' || loading}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                    </svg>
                    {exporting === 'excel' ? 'Exporting…' : `Excel (${stories.length})`}
                  </button>
                  <button className={`${styles.toolbarBtn} ${styles.toolbarBtnPpt}`} onClick={handlePptDownload} disabled={exporting === 'ppt' || loading}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <rect x="2" y="3" width="20" height="14" rx="2" /><path strokeLinecap="round" d="M8 21h8M12 17v4" />
                    </svg>
                    {exporting === 'ppt' ? 'Exporting…' : `PPT (${stories.length})`}
                  </button>
                  <button className={styles.toolbarBtn} onClick={() => setShowImport(true)}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 3v12M7 8l5-5 5 5" />
                    </svg>
                    Import
                  </button>
                  <button className={`${styles.toolbarBtn} ${styles.toolbarBtnDelete}`} onClick={enterSelectionMode}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <polyline points="3 6 5 6 21 6" /><path strokeLinecap="round" strokeLinejoin="round" d="M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" />
                    </svg>
                    Bulk Delete
                  </button>
                  <button className={styles.toolbarBtnPrimary} onClick={() => setStoryForm('new')}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16M4 12h16" />
                    </svg>
                    Add Story
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Results */}
          <section className={styles.resultsSection} aria-label="Story results">
            {loading && <p className={styles.statusMsg}>Loading stories…</p>}
            {error && <p className={styles.errorMsg}>{error}</p>}
            {!loading && !error && stories.length === 0 && (
              <p className={styles.statusMsg}>No stories found. Try adjusting your filters.</p>
            )}
            {!loading && !error && stories.length > 0 && (
              <div className={styles.cardsGrid}>
                {stories.map(story => (
                  <StoryCard
                    key={story.id}
                    story={story}
                    onClick={setSelectedStory}
                    selectionMode={selectionMode}
                    isSelected={selectedIds.has(story.id)}
                    onSelect={toggleSelect}
                  />
                ))}
              </div>
            )}
          </section>

        </div>{/* end mainArea */}
      </div>{/* end pageBody */}

      <Footer />

      {selectedStory && (
        <StoryModal
          story={selectedStory}
          onClose={() => setSelectedStory(null)}
          onDelete={handleDeleteStory}
          onEdit={handleEditStory}
          onDownload={handleStoryDownload}
          exporting={exporting}
        />
      )}
      {storyForm && (
        <AddStoryModal
          filterOptions={filterOptions}
          story={storyForm === 'new' ? null : storyForm}
          onClose={() => setStoryForm(null)}
          onSaved={fetchStories}
        />
      )}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onImported={fetchStories} />}
    </div>
  );
};

export default DBStories;
